import {
    type AnnotationCagrArrow,
    type AnnotationModeContext,
    AnnotationMode,
    AnnotationsRepositoryAdapter,
    AnnotationComment,
    AnnotationHighlight,
    AnnotationCRUDPermissionsMap,
    CommentMetadataMap,
    VisualViewMode,
} from "@zebrabi/annotation-layer";

export interface VisualInstanceAnnotationDataSource {
    getCagrArrows(): AnnotationCagrArrow[];
}

/**
 * Holds the CAGR arrows the add-in hands it through setCachedAnnotations and hands every create, update and delete back
 * through onChange, so the add-in persists them. Whether an arrow can be drawn on the current data is decided by the
 * annotation layer store and the charts renderers, not here. Comments and highlights are not supported in Office.
 */
export class VisualInstanceAnnotationsAdapter extends AnnotationsRepositoryAdapter {
    private cachedCagrArrows: AnnotationCagrArrow[] = [];

    constructor(
        private readonly onChange: (annotationCagrArrows: AnnotationCagrArrow[]) => void,
        dataSource: VisualInstanceAnnotationDataSource,
        private readonly getAnnotationModeContext: () => AnnotationModeContext
    ) {
        super();

        this.setCachedAnnotations(dataSource);
    }

    public createCagrArrow(newCagrArrow: AnnotationCagrArrow): void {
        this.onChange([newCagrArrow]);
    }

    public updateCagrArrow(updatedCagrArrow: AnnotationCagrArrow): void {
        this.onChange([updatedCagrArrow]);
    }

    public deleteCagrArrow(): void {
        this.onChange([]);
    }

    public getCagrArrows(): AnnotationCagrArrow[] {
        return this.cachedCagrArrows;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public createComment(newComment: AnnotationComment): void {
        throw new Error("VisualInstanceAnnotationsAdapter.createComment is not supported.");
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public createHighlight(newHighlight: AnnotationHighlight): void {
        throw new Error("VisualInstanceAnnotationsAdapter.createHighlight is not supported.");
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public deleteComment(commentId: AnnotationComment["uuid"]): void {
        throw new Error("VisualInstanceAnnotationsAdapter.deleteComment is not supported.");
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public deleteHighlight(highlightId: AnnotationHighlight["uuid"]): void {
        throw new Error("VisualInstanceAnnotationsAdapter.deleteHighlight is not supported.");
    }

    /** Mirrors the Power BI charts adapter */
    public getAnnotationPermissions(): AnnotationCRUDPermissionsMap {
        const permissionsMap: AnnotationCRUDPermissionsMap = new Map();
        const modeContext = this.getAnnotationModeContext();
        const canModify =
            modeContext.visualViewMode === VisualViewMode.Edit
            && [AnnotationMode.Edit, AnnotationMode.Actions].includes(modeContext.annotationMode);

        for (const cagrArrow of this.cachedCagrArrows) {
            permissionsMap.set(cagrArrow.uuid, { update: canModify, delete: canModify });
        }

        return permissionsMap;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getCommentMetadata(locale: string): CommentMetadataMap {
        return new Map();
    }

    /** Mirrors the Power BI charts adapter */
    public getWritePermission(): boolean {
        return this.getAnnotationModeContext().visualViewMode === VisualViewMode.Edit;
    }

    public getComments(): AnnotationComment[] {
        return [];
    }

    public getHighlights(): AnnotationHighlight[] {
        return [];
    }

    public setCachedAnnotations(dataSource: VisualInstanceAnnotationDataSource): void {
        this.cachedCagrArrows = dataSource.getCagrArrows();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public updateComment(updatedComment: AnnotationComment): void {
        throw new Error("VisualInstanceAnnotationsAdapter.updateComment is not supported.");
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public updateHighlight(updatedHighlight: AnnotationHighlight): void {
        throw new Error("VisualInstanceAnnotationsAdapter.updateHighlight is not supported.");
    }
}
